import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment, LinearProgress,
  List, ListItemButton, ListItemText, MenuItem,
  Paper, Snackbar, Stack, Step, StepContent, StepLabel, Stepper, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { adminApi, api, ApiError } from '../api/client';
import type { City, Job, PoiImportPreview, SystemStatus, VerifyResult, GeocodedPlace } from '../api/types';
import { JobProgress } from '../components/JobProgress';
import { AdminSection } from '../components/AdminSection';
import { FEATURES } from '../config/features';
import { useSession } from '../components/RequireAuth';
import { AdminNav, type AdminGroup } from '../components/AdminNav';
import { Authorized, Permission, usePermission } from '../features/authorization';

/*
 * Fetched when a module is opened, not when Admin is.
 *
 * These nine panels are some five thousand lines between them, and every one
 * of them used to be parsed on the way to a screen showing one. Behind their
 * own addresses they can be fetched on their own too, which is the part of
 * this the network can see.
 *
 * Named exports, so each import is mapped to the default `lazy` expects
 * rather than the files being rewritten to suit it.
 */
const SettingsPanel = lazy(() =>
  import('../components/SettingsPanel').then((m) => ({ default: m.SettingsPanel })));
const AssistantSettingsPanel = lazy(() =>
  import('../components/AssistantSettingsPanel').then((m) => ({ default: m.AssistantSettingsPanel })));
const AssistantAgentPanel = lazy(() =>
  import('../components/AssistantAgentPanel').then((m) => ({ default: m.AssistantAgentPanel })));
const RouteStylesPanel = lazy(() =>
  import('../components/RouteStylesPanel').then((m) => ({ default: m.RouteStylesPanel })));
const ThemesPanel = lazy(() =>
  import('../components/ThemesPanel').then((m) => ({ default: m.ThemesPanel })));
const CategoriesPanel = lazy(() =>
  import('../components/CategoriesPanel').then((m) => ({ default: m.CategoriesPanel })));
const PlacesPanel = lazy(() =>
  import('../components/PlacesPanel').then((m) => ({ default: m.PlacesPanel })));
const RolesPanel = lazy(() =>
  import('../components/RolesPanel').then((m) => ({ default: m.RolesPanel })));
const UsersPanel = lazy(() =>
  import('../components/UsersPanel').then((m) => ({ default: m.UsersPanel })));

/*
 * Themes are offered again — see the same flag in PlannerPage. Their admin
 * screen comes back with them: a traveller can choose one, so somebody has
 * to be able to edit what it means.
 */
const SHOW_THEMES = true;
/** See the note beside the panel. Styles are edited nowhere for now. */
const SHOW_ROUTE_STYLES = false;

/*
 * The modules, and what the nav makes of them.
 *
 * Defined here rather than in the nav because the routes below are built from
 * the same list: a heading somebody can click and an address that does not
 * exist is the one bug a side nav must not have.
 *
 * Grouped by the question each answers — what the catalogue holds, how
 * journeys are shaped, who may do any of it, and how the system is set up.
 */
/*
 * Which modules this build offers.
 *
 * Entries are dropped from the list rather than drawn disabled: a heading
 * whose group has emptied disappears with it, which the nav already handles,
 * and nothing is left pointing at a screen this build will not route to.
 * See src/config/features.ts.
 */
const ADMIN_GROUPS: AdminGroup[] = [
  {
    heading: 'Catalogue',
    items: [
      ...(FEATURES.cityImport ? [{ slug: 'cities', label: 'Cities' }] : []),
      { slug: 'places', label: 'Places' },
      ...(FEATURES.categoryConfig ? [{ slug: 'categories', label: 'Categories' }] : []),
    ],
  },
  {
    heading: 'Planning',
    items: [
      ...(SHOW_THEMES ? [{ slug: 'themes', label: 'Themes' }] : []),
      ...(SHOW_ROUTE_STYLES ? [{ slug: 'route-styles', label: 'Route styles' }] : []),
    ],
  },
  {
    heading: 'Access',
    items: FEATURES.userManagement ? [
      { slug: 'users', label: 'Users', permission: Permission.USER_MANAGE },
      { slug: 'roles', label: 'Roles', permission: Permission.ROLE_MANAGE },
    ] : [],
  },
  {
    heading: 'System',
    items: [
      /*
        Settings are the deployment's, not a planner's.

        Every control on that screen writes through AdminSettingsService,
        which requires PERMISSION_SETTINGS_WRITE — so a planner who opened
        it could read the defaults and have every save refused. Offered to
        whoever holds that permission, which is the admin role and any role
        given it, rather than to the word "admin".
      */
      ...(FEATURES.plannerSettings
        ? [{ slug: 'settings', label: 'Settings', permission: Permission.SETTINGS_WRITE }]
        : []),
      ...(FEATURES.planningAgent ? [
        { slug: 'assistant', label: 'Assistant' },
        /*
          "Agent" is NOT in this list, and its route below is.

          It is a reference page — the prompt the assistant runs under, the
          shape of a turn, the tools it can reach — read when somebody is
          asking how the thing works, not while they are administering it.
          Beside "Settings" and "Assistant", which are both places you go to
          CHANGE something, it read as a third thing to configure.

          Reachable at /admin/agent, and linked from the Assistant page, which
          is where somebody wondering about it already is.
        */
      ] : []),
    ],
  },
];


/** US timezones, since the MVP targets the United States. */
/*
 * What each country implies for a new city.
 *
 * Timezone and units were previously fixed to the United States — a city added
 * anywhere else was recorded as US and imperial, quietly, and every clock time
 * and distance in its itineraries was then wrong.
 */
export const COUNTRIES = [
  {
    code: 'us',
    label: 'United States',
    /*
     * Kilometres, even here.
     *
     * Every other number in a plan is metric — hold distances, search radii,
     * corridor widths, the proximity radii — so a distance in miles beside
     * them described one journey in two systems. The column still takes
     * `imperial` for a city that genuinely wants it.
     */
    units: 'metric' as const,
    timezones: [
      'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
      'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
    ],
  },
  {
    code: 'in',
    label: 'India',
    units: 'metric' as const,
    // One zone for the whole country, offset by half an hour.
    timezones: ['Asia/Kolkata'],
  },
  {
    code: 'ca',
    label: 'Canada',
    units: 'metric' as const,
    timezones: [
      'America/St_Johns', 'America/Halifax', 'America/Toronto',
      'America/Winnipeg', 'America/Edmonton', 'America/Vancouver',
    ],
  },
  {
    code: 'gb',
    label: 'United Kingdom',
    units: 'metric' as const,
    timezones: ['Europe/London'],
  },
  {
    code: 'au',
    label: 'Australia',
    units: 'metric' as const,
    timezones: [
      'Australia/Sydney', 'Australia/Brisbane', 'Australia/Adelaide',
      'Australia/Perth', 'Australia/Darwin',
    ],
  },
  {
    code: 'mx',
    label: 'Mexico',
    units: 'metric' as const,
    timezones: ['America/Mexico_City', 'America/Cancun', 'America/Tijuana'],
  },
];

const ALL_TIMEZONES = [...new Set(COUNTRIES.flatMap((c) => c.timezones))];

const STATUS_COLOUR = {
  draft: 'default', importing: 'info', ready: 'success', error: 'error',
} as const;

export function AdminPage() {
  /*
    Accounts, where there are accounts.

    With the login off every request already holds every permission, so the
    permission gate below would happily show Users and Roles — screens for
    administering operators of a deployment that has none. The nav items go
    and so do their routes, because an address somebody kept is as easy a way
    in as a link.
  */
  const { session } = useSession();
  const showAccounts = FEATURES.userManagement && !session.authDisabled;
  const groups = useMemo(
    () => (showAccounts
      ? ADMIN_GROUPS
      : ADMIN_GROUPS.map((g) => (g.heading === 'Access' ? { ...g, items: [] } : g))),
    [showAccounts],
  );

  /*
   * The address the modules are routed by, and what keys the boundary below.
   * Taken from the router rather than the `location` the browser already has
   * in scope: they agree most of the time, which is exactly what makes the
   * global the worse of the two — it is not what React re-renders for.
   */
  const location = useLocation();
  /*
   * The module asked for but not yet on screen.
   *
   * Suspense alone could not be relied on for this. A navigation is wrapped
   * in a transition, and a transition's whole purpose is to keep what is
   * already visible rather than replace it with a fallback — so the old
   * module sat there, apparently ignoring the click, until the new one had
   * finished loading. This is answered by the click instead of by React, and
   * cleared when the router's address finally catches up, which is the exact
   * moment the new module is on screen.
   */
  const [pending, setPending] = useState<string | null>(null);
  useEffect(() => { setPending(null); }, [location.pathname]);

  const [system, setSystem] = useState<SystemStatus | null>(null);
  /*
   * Held steady across renders, because the wizard watches it.
   *
   * Built inline it was a new object every render, and the effect that reads
   * it would have re-run on every one of them.
   */
  const unavailableJobs = useMemo(() => system?.unavailableJobs ?? {}, [system]);
  const [cities, setCities] = useState<City[] | null>(null);
  const [selected, setSelected] = useState<City | null>(null);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [settings, setSettings] = useState<Record<string, unknown>>({});

  // Read once here rather than through <Authorized> in each of the several
  // places below that need the plain boolean — a conditional in a ternary, a
  // notice shown only to those without it.
  const canEditCities = usePermission(Permission.CITY_WRITE);

  const refresh = useCallback(async () => {
    try {
      const [status, list, config] = await Promise.all([
        adminApi.system(), adminApi.cities(), api.settings(),
      ]);
      setSystem(status);
      setCities(list);
      setSettings(config);
      setSelected((current) => (current ? list.find((c) => c.id === current.id) ?? null : null));
    } catch (err) {
      setToast((err as ApiError).message);
      setCities([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * Takes a city out of the planner without deleting it.
   *
   * The confirmation names the part that is not instant. Tiles and the
   * basemap are one artefact built from every extract at once, so a city
   * leaves them only when they are rebuilt — and a rebuild is the longest
   * job in the product, with routing unavailable throughout. Starting one
   * behind a button that says "Unpublish" would be a surprise measured in
   * hours, so the rebuild stays a separate, deliberate click.
   */
  const unpublishCity = async (city: City) => {
    if (!window.confirm(
      `Unpublish ${city.name}?

`
      + 'It is hidden from the planner straight away and its extract is removed, '
      + 'so later builds leave it out. The routing tiles and the basemap keep it '
      + 'until you rebuild them — which takes minutes to hours, with routing down '
      + 'while it runs.',
    )) return;

    try {
      const result = await adminApi.unpublishCity(city.id);
      setToast(result.note);
      if (selected?.id === city.id) setSelected(null);
      await refresh();
    } catch (err) {
      setToast((err as ApiError).message);
    }
  };

  const removeCity = async (city: City) => {
    if (!window.confirm(`Delete ${city.name}? Its places and transit graph are removed.`)) return;
    try {
      const result = await adminApi.deleteCity(city.id);
      setToast(result.note);
      if (selected?.id === city.id) setSelected(null);
      await refresh();
    } catch (err) {
      setToast((err as ApiError).message);
    }
  };

  if (cities === null) {
    return <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/*
        The full width of the container, not a column down the middle.

        A 1100px cap was room enough when a module had the page to itself.
        With the nav taking its own 200-odd from the same measure, the rows
        inside a module lost the difference — number fields clipped by their
        own steppers, a group name ending in an ellipsis — so the cap now
        comes off and the modules get the window.
      */}
      <Stack spacing={3} sx={{ width: '100%', mx: 'auto' }}>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          {/*
            Grows into the space, never shrinks out of it.

            minWidth: 0 was here to let the row wrap, and did the opposite for
            a heading: it removes a flex item's default min-width of auto,
            which is the only thing stopping it being squeezed below its own
            text. The buttons kept their size, so the title was what gave way
            — crushed to nothing while "Add city" sat alone on the line it
            should have been pushed off.

            "1 1 auto" grows the same way and refuses to go under the width of
            the words, so the row wraps instead of the heading vanishing.
          */}
          <Typography variant="h1" sx={{ flex: '1 1 auto' }}>City data</Typography>
          <IconButton onClick={refresh} size="small"><RefreshIcon fontSize="small" /></IconButton>
          {/*
            Part of the import pipeline, and hidden with it.

            This button lives in the page header rather than in the Cities
            module, so gating the module left it behind — offering the first
            step of a pipeline whose remaining steps were not on screen.
          */}
          {FEATURES.cityImport && (
            <Authorized permission={Permission.CITY_WRITE}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
                Add city
              </Button>
            </Authorized>
          )}
        </Stack>

        {system && <SystemBar system={system} />}

        {/*
          The nav beside the module, not a column of modules.

          Each one is its own address under /admin, so only the module being
          looked at is mounted — and, being loaded on demand, only that one is
          fetched. What used to be nine panels in a single document, opened and
          shut with accordions, is now nine screens with one on show.
        */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1, md: 3 }}
          sx={{ alignItems: { xs: 'stretch', md: 'flex-start' } }}
        >
          <AdminNav
            groups={groups}
            // Asking for the module already on screen changes no address, so
            // nothing would ever clear the wait it started.
            onNavigate={(slug) => {
              if (!location.pathname.endsWith(`/${slug}`)) setPending(slug);
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            {/*
              Over the outgoing module rather than in place of it: replacing it
              would throw away a screen that is still perfectly readable, and
              the point is only to say that the click was heard.
            */}
            {pending && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'background.default',
                  opacity: 0.75,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            {/*
              Keyed by the address, so each module gets its own boundary.

              A Suspense that merely stays mounted across a navigation is an
              update, not a first mount — and React skips the fallback for an
              update inside a transition, which is what the router wraps every
              navigation in. The effect was a module that hung on screen until
              the next one had finished loading, with nothing to say a click
              had registered. A new key each time makes it a first mount
              again, which is the one case the fallback is always shown for.
            */}
            <Suspense
              key={location.pathname}
              fallback={(
                <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              )}
            >
              <Routes>
                {/* Admin itself is not a screen; the first module is. */}
                <Route index element={<Navigate to={`/admin/${FIRST_MODULE}`} replace />} />

                {FEATURES.cityImport && (
                  <Route
                    path="cities"
                    element={(
                      <>
                        {/*
                          Shown only to an operator who cannot change anything
                          here, so the missing buttons read as a permission
                          rather than a broken page. An admin needs no such
                          notice — they can see the buttons.
                        */}
                        {!canEditCities && (
                          <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
                            You have read-only access to city data. Ask an
                            administrator to import, edit or remove cities.
                          </Alert>
                        )}

                        <AdminSection
                          title="Cities"
                          summary={
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${cities.length} ${cities.length === 1 ? 'city' : 'cities'}`}
                            />
                        }
                      >
        <Paper variant="outlined">
          {cities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              No cities yet. Add one to begin — you will need an OpenStreetMap
              extract URL for the metro area.
            </Typography>
          ) : (
            cities.map((city, index) => (
              <Box key={city.id}>
                {index > 0 && <Divider />}
                {/*
                  Wraps rather than crushing: the name, three chips and up to
                  four buttons do not fit across a phone, and the first thing
                  a fixed row gives up is the name — the one part that says
                  which city the buttons belong to.
                */}
                <Stack
                  direction="row"
                  spacing={2}
                  useFlexGap
                  sx={{ alignItems: 'center', p: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}
                >
                  <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Typography variant="h3">{city.name}</Typography>
                      <Chip size="small" label={city.status} color={STATUS_COLOUR[city.status]} />
                      {city.otpSlot && <Chip size="small" variant="outlined" label={`transit ${city.otpSlot}`} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {city.poiCount.toLocaleString('en-US')} places · {city.timezone}
                      {city.statusMessage ? ` · ${city.statusMessage}` : ''}
                    </Typography>
                  </Box>
                  {/*
                    Offered only where no outline is stored. Imports otherwise
                    keep everything the bounding box catches, which for a
                    diamond-shaped city is mostly other places.
                  */}
                  {!city.hasBoundary && canEditCities && (
                    <Tooltip
                      title="Imports currently keep everything in this city's bounding box, including neighbouring areas. Fetching the real outline trims them to the city."
                      placement="top"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ flexShrink: 0 }}
                        onClick={async () => {
                          try {
                            const r = await adminApi.fetchBoundary(city.id);
                            await refresh();
                            setToast(
                              `${city.name}: ${r.matched.split(',')[0]} — `
                              + `${r.areaKm2.toLocaleString('en-US')} km², `
                              + `${r.percentOfBbox}% of its bounding box`,
                            );
                          } catch (err) {
                            setToast((err as ApiError).message);
                          }
                        }}
                      >
                        Get boundary
                      </Button>
                    </Tooltip>
                  )}
                  <Authorized permission={Permission.CITY_WRITE}>
                    <PoiSourcePicker city={city} onChanged={refresh} onToast={setToast} />
                  </Authorized>
                  {/*
                    Running an import is its own permission: it rewrites the
                    city's places wholesale, which is a heavier thing than
                    editing the city's own fields.
                  */}
                  <Authorized permission={Permission.JOB_RUN}>
                    <Button
                      size="small"
                      variant={selected?.id === city.id ? 'contained' : 'outlined'}
                      onClick={() => setSelected(selected?.id === city.id ? null : city)}
                    >
                      {selected?.id === city.id ? 'Close' : 'Import'}
                    </Button>
                  </Authorized>
                  {/*
                    Offered only where there is something to unpublish. A
                    draft city is already out of the planner, and the button
                    would do nothing anyone could see.
                  */}
                  {city.status === 'ready' && (
                    <Authorized permission={Permission.CITY_WRITE}>
                      <Tooltip title="Hide from the planner and drop its extract, keeping the city and its places">
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ flexShrink: 0 }}
                          onClick={() => void unpublishCity(city)}
                        >
                          Unpublish
                        </Button>
                      </Tooltip>
                    </Authorized>
                  )}
                  <Authorized permission={Permission.CITY_WRITE}>
                    <IconButton size="small" onClick={() => removeCity(city)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Authorized>
                </Stack>

                {selected?.id === city.id && (
                  <Box sx={{ px: 2, pb: 3, bgcolor: 'action.hover' }}>
                    <ImportWizard
                      city={city}
                      onChanged={refresh}
                      onToast={setToast}
                      unavailable={unavailableJobs}
                    />
                  </Box>
                )}
              </Box>
            ))
          )}
        </Paper>
                      </AdminSection>
                    </>
                  )}
                />
                )}
                <Route path="places" element={<PlacesPanel cities={cities} />} />
                {FEATURES.categoryConfig && (
                  <Route path="categories" element={<CategoriesPanel />} />
                )}

                {SHOW_THEMES && <Route path="themes" element={<ThemesPanel />} />}
                {/*
                  Route styles are not offered anywhere a plan is made — the
                  day form hides the picker and a trip is shaped by an emphasis
                  instead — so editing them here would be maintaining something
                  nobody can choose. The rows, the API and every saved route
                  that names one are untouched; one flag brings the panel back,
                  here and in the nav, which reads the same flag.
                */}
                {SHOW_ROUTE_STYLES && (
                  <Route path="route-styles" element={<RouteStylesPanel cities={cities} />} />
                )}

                {/*
                  Access control, behind the permissions that govern it. The
                  nav hides these entries too, but the guard stays: a link is
                  not the only way to reach an address.

                  Two separate permissions, so an operator can be trusted to
                  add accounts without also being able to invent a role that
                  grants everything.
                */}
                {showAccounts && (
                  <Route
                    path="users"
                    element={(
                      <Authorized permission={Permission.USER_MANAGE}>
                        <UsersPanel onToast={setToast} />
                      </Authorized>
                    )}
                  />
                )}
                {showAccounts && (
                  <Route
                    path="roles"
                    element={(
                      <Authorized permission={Permission.ROLE_MANAGE}>
                        <RolesPanel onToast={setToast} />
                      </Authorized>
                    )}
                  />
                )}

                {FEATURES.plannerSettings && (
                  <Route
                    path="settings"
                    element={(
                      <Authorized permission={Permission.SETTINGS_WRITE}>
                        <SettingsPanel
                          settings={settings}
                          system={system}
                          onSaved={refresh}
                          onToast={setToast}
                        />
                      </Authorized>
                    )}
                  />
                )}
                {FEATURES.planningAgent && (
                  <Route
                    path="assistant"
                    element={(
                      <AssistantSettingsPanel
                        settings={settings}
                        onSaved={refresh}
                        onToast={setToast}
                      />
                    )}
                  />
                )}

                {FEATURES.planningAgent && (
                  <Route path="agent" element={<AssistantAgentPanel />} />
                )}

                {/* A stale bookmark lands on the first module, not on nothing. */}
                <Route path="*" element={<Navigate to={`/admin/${FIRST_MODULE}`} replace />} />
              </Routes>
            </Suspense>
          </Box>
        </Stack>
      </Stack>

      {/* Same feature, and nothing else can open it. */}
      {FEATURES.cityImport && (
      <AddCityDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={async (city) => {
          setAdding(false);
          await refresh();
          setSelected(city);
        }}
      />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}

// ── system bar ──────────────────────────────────────────────────────

function SystemBar({ system }: { system: SystemStatus }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <Tooltip title={system.backend.detail}>
          <Chip
            size="small"
            label={system.backend.ok ? 'App healthy' : 'App unhealthy'}
            color={system.backend.ok ? 'success' : 'error'}
            variant="outlined"
          />
        </Tooltip>
        {system.disk && (
          <Tooltip title="Each city costs roughly 8–15 GB across extract, tiles, graph and basemap">
            <Chip
              size="small"
              variant="outlined"
              color={system.disk.lowSpace ? 'warning' : 'default'}
              label={`${system.disk.free} free of ${system.disk.total}`}
            />
          </Tooltip>
        )}
        <Chip
          size="small"
          variant="outlined"
          label={
            system.queue.active
              ? `1 job running${system.queue.queued ? `, ${system.queue.queued} queued` : ''}`
              : 'Queue idle'
          }
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Builds run one at a time
        </Typography>
      </Stack>
    </Paper>
  );
}

// ── import wizard ───────────────────────────────────────────────────

interface WizardProps {
  city: City;
  onChanged: () => Promise<void> | void;
  onToast: (message: string) => void;
  /** Job types this deployment will refuse, keyed by type, with the reason. */
  unavailable: Record<string, string>;
}

const POI_SOURCES: { value: City['poiSource']; label: string; help: string }[] = [
  {
    value: 'osm',
    label: 'OpenStreetMap',
    help: 'Complete for infrastructure and landmarks. No closure data, and thin on restaurants and shops.',
  },
  {
    value: 'fsq',
    label: 'Foursquare',
    help: 'Far more commercial places, and knows what has shut. Safety infrastructure still comes from OpenStreetMap.',
  },
  {
    value: 'both',
    label: 'Both',
    help: 'Every place from both datasets. They overlap, so the same place can appear twice — no deduplication yet.',
  },
];

/**
 * Which dataset a city draws suggestions from.
 *
 * Sits on the city row rather than in Settings because it is a property of
 * the place, not of the installation: OpenStreetMap's commercial coverage is
 * excellent in some metros and thin in others, and Foursquare's varies the
 * other way. Changing it takes effect on the next plan — nothing is
 * re-imported, since both datasets are already in the table.
 */
function PoiSourcePicker({
  city, onChanged, onToast,
}: {
  city: City;
  onChanged: () => Promise<void> | void;
  onToast: (message: string) => void;
}) {
  const [saving, setSaving] = useState(false);

  const choose = async (next: City['poiSource']) => {
    if (next === city.poiSource) return;
    setSaving(true);
    try {
      await adminApi.updateCity(city.id, { poiSource: next });
      await onChanged();
      onToast(`${city.name} now plans from ${POI_SOURCES.find((s) => s.value === next)?.label}`);
    } catch (err) {
      onToast((err as ApiError).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TextField
      select
      size="small"
      label="Places from"
      value={city.poiSource}
      disabled={saving}
      onChange={(e) => void choose(e.target.value as City['poiSource'])}
      // 180 where there is room; the full width of a wrapped row where
      // there is not, rather than a field narrower than its own label.
      sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
      slotProps={{
        select: {
          /*
           * The closed field shows the label alone; the menu shows what each
           * option costs. The explanation lives in the options rather than a
           * tooltip on the field because a tooltip rendered over the open menu
           * and swallowed the clicks — and because the moment you need the
           * help is while choosing, not while looking at the current value.
           */
          renderValue: (value: unknown) =>
            POI_SOURCES.find((s) => s.value === value)?.label ?? String(value),
          MenuProps: { slotProps: { paper: { sx: { maxWidth: 340 } } } },
        },
      }}
    >
      {POI_SOURCES.map((s) => (
        <MenuItem key={s.value} value={s.value} sx={{ display: 'block', py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', whiteSpace: 'normal' }}
          >
            {s.help}
          </Typography>
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * Which wizard step each job type belongs to.
 *
 * Needed to put a reattached job back under the right heading — a job knows
 * what it is, but not where the wizard draws it.
 */
const JOB_STEP: Record<string, number> = {
  osm_download: 0,
  poi_import: 1,
  fsq_import: 2,
  valhalla_build: 3,
  otp_build: 4,
  basemap_build: 5,
  verify: 6,
};

/** A job still going, or about to. */
const JOB_IN_FLIGHT = new Set(['queued', 'running']);

/*
FIRST_MODULE is where /admin lands, and where a stale address is sent.

Both used to name "cities" outright, which was fine while it always existed.
With the import pipeline hidden it does not, and the two redirects pointed at
a route that fell through to the catch-all, which pointed back at it — /admin
became an infinite redirect rather than a page.

Read off the same list the nav draws, so it is whatever this build offers
first and cannot name a module that is not routed.
*/
const FIRST_MODULE = ADMIN_GROUPS.flatMap((group) => group.items)[0]?.slug ?? 'places';

function ImportWizard({ city, onChanged, onToast, unavailable }: WizardProps) {
  /* Which step of the pipeline transit is, for the hiding above. */
  const TRANSIT_STEP = 4;
  const [step, setStep] = useState(0);
  const [osmUrl, setOsmUrl] = useState('');
  const [osmUpload, setOsmUpload] = useState<{ name: string; percent: number } | null>(null);
  const [activeJob, setActiveJob] =
    useState<{ id: string; step: number; done?: boolean } | null>(null);
  const [preview, setPreview] = useState<PoiImportPreview | null>(null);
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [gtfsFiles, setGtfsFiles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  /*
   * Pick up a job that is already running.
   *
   * The wizard's job id was component state, so closing the panel and
   * reopening it showed step 1 with an empty URL box — as though a download
   * that was minutes in had never been started. The job itself was fine all
   * along; only the view of it was lost, which is worse than a failure
   * because it invites starting the whole thing again.
   *
   * The queue is serial, so at most one job can be in flight; the newest
   * in-flight one is the one to show. JobProgress replays the entire log on
   * connect, so reattaching mid-build shows everything from the beginning
   * rather than only what happens next.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const jobs = await adminApi.jobs(city.id);
        const running = jobs.find((job) => JOB_IN_FLIGHT.has(job.status));

        /*
         * Failing that, the last one that ran.
         *
         * Reopening this panel after a build finished used to show a step
         * with nothing under it, which says the same thing as never having
         * run it. The row is in the database either way, so the last run's
         * status and log are readable here for as long as they are the last
         * run — the wizard's step is not moved for it, only made readable.
         */
        const show = running ?? jobs.find((job) => !unavailable[job.type]);
        if (cancelled || !show) return;

        const stepIndex = JOB_STEP[show.type] ?? 0;

        // Never over the top of a job being watched right now: this also runs
        // when the deployment's constraints arrive, which can be at any moment.
        setActiveJob((current) => (
          current && !current.done
            ? current
            : { id: show.id, step: stepIndex, done: !running }
        ));
        if (running) setStep(stepIndex);
      } catch {
        // Nothing to reattach to is the normal case, and a failure here must
        // not stop the wizard being used to start something new.
      }
    })();

    return () => { cancelled = true; };
  }, [city.id, unavailable]);

  const start = async (index: number, launch: () => Promise<Job>) => {
    setBusy(true);
    try {
      const job = await launch();
      setActiveJob({ id: job.id, step: index });
    } catch (err) {
      onToast((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  /*
   * A finished job stays on screen.
   *
   * This used to clear it the instant a terminal status arrived, which
   * unmounted the progress view in the same render that produced the only
   * thing anyone was waiting for: the final status, the closing log lines
   * and, on a failure, the message saying what to do about it. All of it
   * existed for no frames at all. A ten-minute tile build would run, the
   * panel would vanish, and the log had to be dug out of the database.
   *
   * It is replaced when the next job starts, and until then it is the record
   * of what the last one did.
   */
  const handleFinished = async (job: Job) => {
    setActiveJob((current) => (current ? { ...current, done: true } : current));
    await onChanged();

    if (job.status !== 'succeeded') {
      onToast(job.message ?? 'The job did not complete');
      return;
    }
    if (job.type === 'poi_import' && (job.result as PoiImportPreview)?.preview) {
      setPreview(job.result as PoiImportPreview);
      return;
    }
    if (job.type === 'verify') {
      setVerify(job.result as VerifyResult);
    }
    setStep((current) => Math.min(current + 1, 7));
  };

  /*
   * The same import, from a file the operator already has.
   *
   * Two things make this worth having beside the URL box. A file built at
   * bbbike arrives by email as a download, not as a link anything else can
   * fetch; and a server that cannot reach the open internet — or reaches it
   * through a proxy that mangles a 300 MB redirect chain — cannot fetch a
   * link at all, while a browser on the operator's desk can.
   *
   * The transfer is the long part and the server's half is a rename, so
   * progress is reported from here while it runs and the job panel takes over
   * for the outcome. It lands in the same activeJob slot a download lands in:
   * the step completes, the city refreshes and the log reads the same either
   * way, because it IS the same job row.
   */
  const uploadOsm = async (file: File | null) => {
    if (!file) return;

    setBusy(true);
    setOsmUpload({ name: file.name, percent: 0 });
    try {
      const job = await adminApi.uploadOsm(city.id, file, (fraction) => {
        setOsmUpload({ name: file.name, percent: Math.round(fraction * 100) });
      });
      setActiveJob({ id: job.id, step: 0 });
    } catch (err) {
      onToast((err as ApiError).message);
    } finally {
      setBusy(false);
      setOsmUpload(null);
    }
  };

  const uploadGtfs = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const result = await adminApi.uploadGtfs(city.id, file);
        setGtfsFiles((prev) => [...prev, `${result.filename} (${result.size})`]);
      }
    } catch (err) {
      onToast((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  /** A job still going. A finished one is only on display and blocks nothing. */
  const blocking = Boolean(activeJob && !activeJob.done);

  /**
   * The refusal for a step's job, or null when this deployment can run it.
   *
   * Read from the API rather than inferred from anything here: the same
   * refusal the job itself would raise, so the panel cannot grey out a button
   * that would have worked, or offer one that cannot.
   */
  const refusedAt = (index: number): string | null => {
    const entry = Object.entries(JOB_STEP).find(([, step]) => step === index);
    return (entry && unavailable[entry[0]]) || null;
  };

  /*
   * The job panel for a step, unless the deployment has already explained
   * that step away.
   *
   * A refused job type leaves a failed row behind from whenever somebody last
   * pressed the button, and reattaching it puts the red failure back under the
   * note that exists to replace it — saying twice, in two registers, something
   * that is not a fault at all. The explanation is the better of the two, so
   * it stands alone. A job actually running is always shown, refused or not,
   * because then something really is happening.
   */
  const jobFor = (index: number) =>
    activeJob?.step === index && !(activeJob.done && refusedAt(index)) ? (
      <Box sx={{ mt: 1.5 }}>
        <JobProgress
          jobId={activeJob.id}
          /*
           * Only a job watched from the start reports. One already finished
           * when this panel opened is history: advancing the wizard or
           * raising a toast for it would re-run yesterday's outcome every
           * time the panel is opened.
           */
          onFinished={activeJob.done ? undefined : handleFinished}
        />
      </Box>
    ) : null;

  return (
    /*
      The number MUI shows, which is not the number the jobs use.

      Step indices are the server's: `jobFor(4)` means the transit job
      whether or not the step is drawn. Hiding one shifts every step after it
      up by a place on screen, so the highlight has to be told.
    */
    <Stepper
      activeStep={FEATURES.transitImport || step <= TRANSIT_STEP ? step : step - 1}
      orientation="vertical"
      nonLinear
      sx={{ pt: 2 }}
    >
      {/* 1 ── OSM extract */}
      <Step completed={city.hasExtract} expanded={step === 0 || activeJob?.step === 0}>
        <StepLabel onClick={() => setStep(0)} sx={{ cursor: 'pointer' }}>
          Map data
        </StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              This city needs a <code>.osm.pbf</code> extract covering it. Build
              a custom metro area at{' '}
              <a href="https://extract.bbbike.org" target="_blank" rel="noreferrer">extract.bbbike.org</a>,
              or use a state file from{' '}
              <a href="https://download.geofabrik.de/north-america/us.html" target="_blank" rel="noreferrer">Geofabrik</a>.
              Paste a link and the server fetches it itself, which is the faster
              way when it can reach the file; upload one instead when you already
              have the extract, or when the server cannot reach where it lives.
            </Typography>
            <TextField
              fullWidth
              label="Extract URL"
              placeholder="https://download.geofabrik.de/north-america/us/district-of-columbia-latest.osm.pbf"
              value={osmUrl}
              onChange={(e) => setOsmUrl(e.target.value)}
            />
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                disabled={!osmUrl.trim() || busy || blocking || Boolean(refusedAt(0))}
                onClick={() => start(0, () => adminApi.importOsm(city.id, osmUrl.trim()))}
              >
                Download
              </Button>
              <Typography variant="body2" color="text.secondary">or</Typography>
              {/* Refused with the download: both end at the same file on the
                  same volume, so a deployment that cannot take one cannot take
                  the other either. */}
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                disabled={busy || blocking || Boolean(refusedAt(0))}
              >
                Upload a .osm.pbf
                <input
                  hidden
                  type="file"
                  accept=".pbf,.osm.pbf"
                  onChange={(e) => {
                    void uploadOsm(e.target.files?.[0] ?? null);
                    // Cleared so choosing the same file again still fires a
                    // change — the usual thing to do after a failed upload.
                    e.target.value = '';
                  }}
                />
              </Button>
            </Stack>
            {osmUpload && (
              <Box>
                <LinearProgress variant="determinate" value={osmUpload.percent} />
                <Typography variant="caption" color="text.secondary">
                  {/* The last percent is the browser's, not the server's: the
                      file is still being checked and moved into place after
                      the final byte goes out. */}
                  {osmUpload.percent < 100
                    ? `Uploading ${osmUpload.name} — ${osmUpload.percent}%`
                    : `Checking ${osmUpload.name}`}
                </Typography>
              </Box>
            )}
            {refusedAt(0) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(0)}
              </Alert>
            )}
            {jobFor(0)}
          </Stack>
        </StepContent>
      </Step>

      {/* 2 ── POIs */}
      <Step completed={city.poiCount > 0} expanded={step === 1 || activeJob?.step === 1}>
        <StepLabel onClick={() => setStep(1)} sx={{ cursor: 'pointer' }}>
          Places {city.poiCount > 0 && `· ${city.poiCount.toLocaleString('en-US')} imported`}
        </StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Extracts places from the map data using the OpenStreetMap tag
              mapping. Preview first to see what will be imported.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                disabled={busy || blocking || Boolean(refusedAt(1))}
                onClick={() => { setPreview(null); void start(1, () => adminApi.importPois(city.id, { preview: true })); }}
              >
                Preview
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={busy || blocking || Boolean(refusedAt(1))}
                onClick={() => start(1, () => adminApi.importPois(city.id, { preview: false }))}
              >
                Import places
              </Button>
            </Stack>

            {preview && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{preview.total.toLocaleString('en-US')}</strong> places found
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {preview.breakdown.slice(0, 16).map((item) => (
                    <Chip
                      key={item.categoryKey}
                      size="small"
                      variant="outlined"
                      label={`${item.categoryKey.replace(/_/g, ' ')} ${item.count}`}
                    />
                  ))}
                </Stack>
              </Paper>
            )}
            {refusedAt(1) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(1)}
              </Alert>
            )}
            {jobFor(1)}
          </Stack>
        </StepContent>
      </Step>

      {/* 3 ── Foursquare places (optional) */}
      <Step expanded={step === 2 || activeJob?.step === 2}>
        <StepLabel
          optional={<Typography variant="caption">Optional</Typography>}
          onClick={() => setStep(2)}
          sx={{ cursor: 'pointer' }}
        >
          Foursquare places
        </StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              A second dataset, far richer in restaurants, shops and bars, and the
              only one that knows what has closed down. Downloads just this
              city&apos;s bounding box from the published release — the release is
              10.7&nbsp;GB, so only the parts that could contain this city are ever
              fetched.
            </Typography>

            <Alert severity="info" variant="outlined">
              This <strong>adds</strong> places rather than replacing the
              OpenStreetMap ones. Choose which a city plans from with{' '}
              <strong>Places from</strong> on its row above — and note that
              hospitals, police and fire stations always come from OpenStreetMap,
              whichever you pick.
            </Alert>

            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={busy || blocking || Boolean(refusedAt(2))}
                onClick={() => start(2, () => adminApi.importFsq(city.id))}
              >
                Import Foursquare places
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Needs <code>HUGGINGFACE_TOKEN</code> in <code>.env</code> the first
              time — the data is Apache&nbsp;2.0 but the download is gated. Once a
              city&apos;s extract is cached under <code>data/fsq/</code>, importing
              again never touches the network.
            </Typography>

            {refusedAt(2) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(2)}
              </Alert>
            )}
            {jobFor(2)}
          </Stack>
        </StepContent>
      </Step>

      {/* 4 ── routing */}
      <Step completed={city.hasRoutingTiles} expanded={step === 3 || activeJob?.step === 3}>
        <StepLabel onClick={() => setStep(3)} sx={{ cursor: 'pointer' }}>Routing tiles</StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Alert severity="info" variant="outlined">
              All cities share one Valhalla tile set, so this rebuilds tiles for
              every city — a few minutes at metro scale. Valhalla is unavailable
              while it runs.
            </Alert>
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={busy || blocking || Boolean(refusedAt(3))}
                onClick={() => start(3, () => adminApi.buildValhalla(city.id))}
              >
                Build routing tiles
              </Button>
            </Box>
            {refusedAt(3) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(3)}
              </Alert>
            )}
            {jobFor(3)}
          </Stack>
        </StepContent>
      </Step>

      {/* 4 ── transit. Hidden where there is no OpenTripPlanner to build with. */}
      {FEATURES.transitImport && (
      <Step expanded={step === 4 || activeJob?.step === 4}>
        <StepLabel
          optional={<Typography variant="caption">Optional</Typography>}
          onClick={() => setStep(4)}
          sx={{ cursor: 'pointer' }}
        >
          Public transport {city.gtfsFeedEndDate && `· feed ends ${city.gtfsFeedEndDate}`}
        </StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Upload one or more GTFS <code>.zip</code> feeds, then build the
              transit graph. Feeds usually expire after 30–90 days; an expired
              feed makes transit return no results rather than an error.
            </Typography>
            <Box>
              {/* Refused with the build itself: feeds nothing can build from
                  are just files taking up the volume. */}
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={busy || Boolean(refusedAt(4))}
              >
                Upload GTFS
                <input hidden type="file" accept=".zip" multiple onChange={(e) => uploadGtfs(e.target.files)} />
              </Button>
            </Box>
            {gtfsFiles.length > 0 && (
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {gtfsFiles.map((name) => <Chip key={name} size="small" label={name} />)}
              </Stack>
            )}
            <Alert severity="warning" variant="outlined">
              Graph builds take 10–40 minutes and are memory hungry. Only three
              cities can have transit at once — OpenTripPlanner 2 serves one
              city per instance.
            </Alert>
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={busy || blocking || Boolean(refusedAt(4))}
                onClick={() => start(4, () => adminApi.buildOtp(city.id))}
              >
                Build transit graph
              </Button>
            </Box>
            {refusedAt(4) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(4)}
              </Alert>
            )}
            {jobFor(4)}
          </Stack>
        </StepContent>
      </Step>
      )}

      {/* 5 ── basemap */}
      <Step completed={city.hasBasemap} expanded={step === 5 || activeJob?.step === 5}>
        <StepLabel
          optional={<Typography variant="caption">Optional</Typography>}
          onClick={() => setStep(5)}
          sx={{ cursor: 'pointer' }}
        >
          Basemap tiles
        </StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Generates a self-hosted vector basemap. Without it the planner
              still works, but routes are drawn on a plain background. The first
              run downloads about 1 GB of auxiliary data.
            </Typography>
            {/*
              Said plainly, because the button does more than its step implies:
              one archive covers every city, which is what lets the planner
              switch between them without changing map — and the price is that
              adding a city rebuilds all of it.
            */}
            <Alert severity="info" variant="outlined">
              One map covers every city, so this rebuilds the whole thing —
              every city with an extract, not just this one. That is what lets
              the planner move between cities without reloading the map.
            </Alert>
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={busy || blocking || Boolean(refusedAt(5))}
                onClick={() => start(5, () => adminApi.buildBasemap(city.id))}
              >
                Rebuild the map
              </Button>
            </Box>
            {refusedAt(5) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(5)}
              </Alert>
            )}
            {jobFor(5)}
          </Stack>
        </StepContent>
      </Step>

      {/* 6 ── verify */}
      <Step completed={city.status === 'ready'} expanded={step === 6 || activeJob?.step === 6}>
        <StepLabel onClick={() => setStep(6)} sx={{ cursor: 'pointer' }}>Verify and publish</StepLabel>
        <StepContent>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Runs a real route in every travel mode and checks each engine. The
              city only becomes selectable in the planner once this passes.
            </Typography>
            <Box>
              <Button
                size="small"
                variant="contained"
                disabled={busy || blocking || Boolean(refusedAt(6))}
                onClick={() => { setVerify(null); void start(6, () => adminApi.verify(city.id)); }}
              >
                Run verification
              </Button>
            </Box>

            {verify && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" gutterBottom color={verify.passed ? 'success.main' : 'error.main'}>
                  {verify.passed ? 'All required checks passed' : 'Some required checks failed'}
                </Typography>
                <Stack spacing={0.5}>
                  {verify.checks.map((check) => (
                    <Stack
                      key={check.name}
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ flexWrap: 'wrap', alignItems: 'baseline' }}
                    >
                      <Chip
                        size="small"
                        label={check.ok ? 'pass' : check.required ? 'fail' : 'warn'}
                        color={check.ok ? 'success' : check.required ? 'error' : 'warning'}
                        variant="outlined"
                        sx={{ width: 62, flexShrink: 0 }}
                      />
                      {/*
                        The name keeps its column where there is one and gives
                        it up where there is not: 62 + 170 + a detail does not
                        fit across a phone, and a fixed column would push the
                        detail off the side rather than onto the next line.
                      */}
                      <Typography variant="caption" sx={{ minWidth: { xs: 0, sm: 170 } }}>
                        {check.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{check.detail}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
            {refusedAt(6) && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                {refusedAt(6)}
              </Alert>
            )}
            {jobFor(6)}
          </Stack>
        </StepContent>
      </Step>
    </Stepper>
  );
}

// ── add city ────────────────────────────────────────────────────────

function AddCityDialog({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (city: City) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [bbox, setBbox] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Boundary lookup, so the box is exact rather than drawn by eye. */
  const [lookup, setLookup] = useState('');
  const [matches, setMatches] = useState<GeocodedPlace[] | null>(null);
  const [searching, setSearching] = useState(false);
  /*
   * How far to grow a found boundary, in kilometres.
   *
   * An administrative boundary is almost never the right search area: Boston's
   * excludes Cambridge and Somerville, which any visitor would count as part of
   * the trip. Too tight is far worse than too loose — the optimiser discards a
   * far-flung candidate on travel time anyway, but a place outside the box can
   * never be suggested at all.
   */
  const [padKm, setPadKm] = useState(0);
  /* US-first, like the rest of the MVP. Empty searches every country. */
  const [country, setCountry] = useState('us');
  const [units, setUnits] = useState<'imperial' | 'metric'>('imperial');
  /*
   * What kind of boundary to look for.
   *
   * "City" is the usual answer and keeps a search for Washington from offering
   * the state. "Anything" widens it to counties too, which are often a better
   * fit for a metro area than the city inside them.
   */
  const [feature, setFeature] = useState('city');

  /*
   * Changing the country moves the timezone and units with it, unless the
   * chosen timezone already belongs to the new country. Leaving them behind is
   * how a Mumbai route ends up timed in New York and measured in miles.
   */
  const chooseCountry = (code: string) => {
    setCountry(code);
    const entry = COUNTRIES.find((c) => c.code === code);
    if (!entry) return;
    setUnits(entry.units);
    if (!entry.timezones.includes(timezone)) setTimezone(entry.timezones[0]!);
  };

  const timezoneOptions =
    COUNTRIES.find((c) => c.code === country)?.timezones ?? ALL_TIMEZONES;

  const search = async () => {
    if (lookup.trim().length < 2) return;
    setSearching(true);
    setError(null);
    try {
      setMatches((await adminApi.geocode(lookup.trim(), country, feature)).results);
    } catch (err) {
      setError((err as ApiError).message);
      setMatches([]);
    } finally {
      setSearching(false);
    }
  };

  const applyMatch = (place: GeocodedPlace) => {
    const [minLng, minLat, maxLng, maxLat] = place.bbox;
    // A degree of latitude is ~111 km everywhere; longitude shrinks towards
    // the poles, so the east-west padding is widened to match on the ground.
    const padLat = padKm / 111;
    const padLng = padKm / (111 * Math.cos((place.lat * Math.PI) / 180));
    const box = [minLng - padLng, minLat - padLat, maxLng + padLng, maxLat + padLat];
    setBbox(box.map((n) => n.toFixed(4)).join(', '));

    const city = place.displayName.split(',')[0]?.trim() ?? '';
    if (!name) {
      setName(city);
      setSlug(city.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
    setMatches(null);
  };

  const parsedBbox = (() => {
    const parts = bbox.split(',').map((p) => Number(p.trim()));
    return parts.length === 4 && parts.every(Number.isFinite)
      ? (parts as [number, number, number, number])
      : null;
  })();

  const submit = async () => {
    if (!parsedBbox) return;
    setSaving(true);
    setError(null);
    try {
      const city = await adminApi.createCity({
        name: name.trim(),
        slug: slug.trim(),
        timezone,
        units,
        countryCode: (country || 'us').toUpperCase(),
        bbox: parsedBbox,
      });
      setName(''); setSlug(''); setBbox('');
      onCreated(city);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a city</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/*
            Looking the boundary up beats drawing one: it is exact, repeatable,
            and comes from the same OpenStreetMap data the city's extract will
            be cut from.
          */}
          <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Find a place"
                sx={{ flex: '1 1 200px', minWidth: 160 }}
                placeholder="Boston, or Travis County Texas"
                value={lookup}
                onChange={(e) => setLookup(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
              />
              <TextField
                select
                size="small"
                label="In"
                value={country}
                onChange={(e) => chooseCountry(e.target.value)}
                sx={{ width: 132, flexShrink: 0 }}
              >
                {COUNTRIES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                ))}
                <MenuItem value="">Anywhere</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Kind"
                value={feature}
                onChange={(e) => setFeature(e.target.value)}
                sx={{ width: 116, flexShrink: 0 }}
              >
                <MenuItem value="city">City</MenuItem>
                <MenuItem value="state">State</MenuItem>
                <MenuItem value="settlement">Town / village</MenuItem>
                <MenuItem value="">Anything</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Widen by"
                value={padKm}
                onChange={(e) => setPadKm(Math.max(0, Math.min(50, Number(e.target.value))))}
                slotProps={{
                  htmlInput: { min: 0, max: 50, step: 1 },
                  input: { endAdornment: <InputAdornment position="end">km</InputAdornment> },
                }}
                sx={{ width: 118, flexShrink: 0 }}
              />
              <Button
                variant="outlined"
                onClick={() => void search()}
                disabled={searching || lookup.trim().length < 2}
                sx={{ flexShrink: 0 }}
              >
                {searching ? '…' : 'Find'}
              </Button>
            </Stack>

            {matches !== null && (
              matches.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Nothing found. Try adding the state, or search the county instead.
                </Typography>
              ) : (
                <List dense sx={{ mt: 0.5 }}>
                  {matches.map((place) => (
                    <ListItemButton
                      key={`${place.displayName}-${place.lat}`}
                      onClick={() => applyMatch(place)}
                    >
                      <ListItemText
                        primary={place.displayName}
                        secondary={
                          padKm > 0
                            ? `${place.kind} · widened by ${padKm} km`
                            : `${place.kind} · exact boundary`
                        }
                        slotProps={{
                          primary: { variant: 'body2', noWrap: true },
                          secondary: { variant: 'caption' },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )
            )}
          </Box>

          <TextField
            label="Name"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Derive the slug until the user edits it themselves.
              setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
            }}
          />
          <TextField
            label="Slug"
            fullWidth
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            helperText="Used for file names — lowercase, digits and hyphens"
          />
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Timezone"
              fullWidth
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              helperText="Every clock time in an itinerary is shown in this zone"
            >
              {timezoneOptions.map((tz) => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Units"
              value={units}
              onChange={(e) => setUnits(e.target.value as 'imperial' | 'metric')}
              sx={{ width: 150, flexShrink: 0 }}
            >
              <MenuItem value="imperial">Miles</MenuItem>
              <MenuItem value="metric">Kilometres</MenuItem>
            </TextField>
          </Stack>
          <TextField
            label="Bounding box"
            fullWidth
            value={bbox}
            onChange={(e) => setBbox(e.target.value)}
            placeholder="-77.125, 38.79, -76.905, 39.00"
            error={bbox.length > 0 && !parsedBbox}
            helperText={
              parsedBbox
                ? 'minLng, minLat, maxLng, maxLat'
                : 'Four numbers: minLng, minLat, maxLng, maxLat. Use Find above, or bboxfinder.com.'
            }
          />
          {error && <Alert severity="error">{error}</Alert>}
          {saving && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!name || !slug || !parsedBbox || saving}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
